import React from 'react'
import { NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useTranslation } from 'react-i18next'
import { wrapper } from 'src/redux'
import {
  Box,
  Heading,
  Text,
  Flex,
} from '@chakra-ui/react'
import { theme } from 'src/themes'
import { useRouter } from 'next/router'
import { Head, LinkRel, MetaTags } from 'src/components/common'
import { ChevronLeftIcon } from '@chakra-ui/icons'
import { TextLink } from 'src/uikit'
import { getFaqItemData } from 'src/utils/getStaticProps'
import { PAGE_CACHE_CONTROL } from 'server/constants'

const Breadcrumbs = dynamic(() => import('src/components/Breadcrumbs'))
const Autocomplete = dynamic(() => import('src/components/Autocomplete'))

interface Props {
  faq: {
    title: string,
    items: Array<any>
  }
  searchAutoCompleteItems: Array<any>
  slug: string
}

const FaqNltPage: NextPage<Props> = ({ faq, searchAutoCompleteItems, slug }) => {
  const { t } = useTranslation()
  const router = useRouter()

  const { items, title } = faq

  const crumbs = [
    {
      page: 'Home Page',
      href: '/',
      title: 'Home Page',
    },
    {
      page: 'Noleggio a Lungo Termine',
      href: '/noleggio-a-lungo-termine',
      title: 'Noleggio a Lungo Termine',
    },
    {
      page: 'FAQ',
      href: '/noleggio-a-lungo-termine/faq',
      title: 'FAQ',
    },
    {
      page: title,
      href: router.asPath.split('#')[0],
      title,
    },
  ]

  return <Box bgColor="baseBackgroundLight" mt="-2">
    <Head title={title} />
    <MetaTags meta={t('faqPage:meta', { returnObjects: true })} />
    <LinkRel rel="canonical" href={`https://www.carplanner.com/noleggio-a-lungo-termine/faq/${slug}`} />

    <Box textAlign="center" bg="primaryGreen.50" position="sticky" top="-xxl2" zIndex="9">
      <Box maxW="7xl" py="l" mx="auto">
        <Flex display={{ base: 'none', md: 'flex' }} mx="-1.5rem" mt="-5">
          <Breadcrumbs items={crumbs} />
        </Flex>
        <Flex display={{ base: 'flex', md: 'none' }} mx="m">
          <TextLink
            href="/noleggio-a-lungo-termine/faq"
            color="baseDark"
            fontSize=".875rem"
            title="Torna all'elenco delle FAQ"
          >
            <ChevronLeftIcon /> FAQ
          </TextLink>
        </Flex>

        <Heading as="h1" fontSize="2xl" fontFamily="Montserrat-SemiBold" color="baseDark">
          {title}
        </Heading>

        <Autocomplete
          items={searchAutoCompleteItems.map(item => ({
            label: `${item.q} - ${item.a}`,
            value: `/noleggio-a-lungo-termine/faq/${item.uri}#${item.anchor}`
          }))}
          onSelect={selected => router.push(selected)}
          placeholder={t('faqPage:placeholder')}
          notFoundText={t('pageHeaderContainer:nothingFound')}
          mt="l"
          mx="auto"
          w="50%"
          minW="300px"
        />
      </Box>
    </Box>

    <Box maxW="7xl" py="l" mx="auto">
      {items.map(item =>
        <Box
          key={item.anchor}
          id={item.anchor}
          bg="baseWhite"
          p="l"
          my="s"
          boxShadow={theme.shadows.cards}
          textAlign="left"
          scrollMarginTop="10em"
        >
          <Text fontFamily="Montserrat-SemiBold">
            {item.q}
          </Text>
          <Box
            fontFamily="Montserrat-Medium"
            className="faq-answer"
            dangerouslySetInnerHTML={{ __html: item.a }}
          />
        </Box>
      )}

    </Box>
  </Box >
}

export const getServerSideProps = wrapper.getServerSideProps(() => async ({ params, res }) => {
  let { slug } = params
  slug = Array.isArray(slug) ? slug[0] : slug
  const content = await getFaqItemData(slug)

  res.setHeader("Cache-Control", PAGE_CACHE_CONTROL);

  return { props: { ...content, slug } }
});

export default FaqNltPage
